import { useEffect, useState, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useCurrentUser, useCreateUser } from "../../hooks/useUser";
import { useAuthProvider } from "./auth-provider";
import { User } from "../../models/User";
import { UserType } from "../../models/UserType";

export const useEnsureUser = () => {
	const { user, setUser } = useAuthProvider();
	const queryClient = useQueryClient();
	const [hasChecked, setHasChecked] = useState(false);
	const [isCreating, setIsCreating] = useState(false);
	const [error, setError] = useState<Error | null>(null);
	const attemptRef = useRef(false);

	const {
		data: foundUser,
		isLoading,
		error: queryError
	} = useCurrentUser(!hasChecked && !isCreating && !attemptRef.current);

	const { mutate: createUserMutation } = useCreateUser();

	useEffect(() => {
		if (queryError && !hasChecked) {
			console.error("❌ Error fetching user:", queryError);
			setError(queryError);
			setHasChecked(true);
			attemptRef.current = true;
		}
	}, [queryError, hasChecked]);

	// Function to update the user while preserving roles and token data
	const updateUserPreservingRoles = useCallback(
		(newUserData: User) => {
			// Preserve the current user's roles
			const roles = user.roles || [];

			// Preserve firstName and lastName from token if not in API data
			const updatedUser = {
				...newUserData,
				roles: newUserData.roles ? [...newUserData.roles] : [...roles],
				// Keep token data if API doesn't provide it
				firstName: newUserData.firstName || user.firstName || "",
				lastName: newUserData.lastName || user.lastName || ""
			};

			// Update with role preservation
			setUser(updatedUser);
		},
		[user.roles, user.firstName, user.lastName, setUser]
	);

	// User creation with role preservation
	const handleCreateUser = useCallback(
		(newUser: User) => {
			createUserMutation(newUser, {
				onSuccess: createdUser => {
					updateUserPreservingRoles(createdUser);

					queryClient.setQueryData(["currentUser"], createdUser);

					if (createdUser.type === UserType.Freelancer) {
						queryClient.invalidateQueries({
							queryKey: ["freelancer"]
						});

						queryClient.invalidateQueries({
							queryKey: ["currentFreelancer"]
						});

						queryClient.invalidateQueries({
							queryKey: ["freelancerProfile"]
						});
					}
				},
				onSettled: () => {
					setHasChecked(true);
					setIsCreating(false);
				},
				onError: err => {
					console.error("❌ Error creating user:", err);
					setError(err as Error);
					setHasChecked(true);
					setIsCreating(false);
				}
			});
		},
		[createUserMutation, queryClient, updateUserPreservingRoles]
	);

	useEffect(() => {
		if (isLoading || isCreating || hasChecked || attemptRef.current) {
			return;
		}

		attemptRef.current = true;

		if (foundUser) {
			updateUserPreservingRoles(foundUser);

			setHasChecked(true);
		} else if (foundUser === null && !error) {
			setIsCreating(true);

			const newUser: User = {
				id: 0,
				externalId: user.externalId,
				email: user.email,
				firstName: user.firstName,
				lastName: user.lastName,
				type: user.type,
				companyId: user.companyId,
				createdAt: new Date().toISOString(),
				updatedAt: undefined,
				company: undefined,
				roles: user.roles ? [...user.roles] : [],
				settings: user.settings
			};

			handleCreateUser(newUser);
		} else if (queryError) {
			console.error("❌ Error checking user:", queryError);
			setHasChecked(true);
		}
	}, [
		foundUser,
		isLoading,
		hasChecked,
		isCreating,
		queryError,
		error,
		handleCreateUser,
		updateUserPreservingRoles,
		user.externalId,
		user.email,
		user.firstName,
		user.lastName,
		user.type,
		user.companyId,
		user.roles
	]);

	return {
		user: foundUser || user,
		isLoading: isLoading || isCreating || (!hasChecked && !error),
		error
	};
};
